import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Text from '../../typography/Text/Text';
import media from '../../styles/media';

const OverlayNoteBoxContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(239, 240, 238, 0.95);
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  max-width: 95%;
  width: 700px;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
  
  ${media.tablet`
    width: 90%;
    max-width: 600px;
    bottom: 30px;
  `}
  
  ${media.phone`
    width: 95%;
    max-width: none;
    padding: 10px 16px;
    bottom: 50px;
  `}
`;

const NoteText = styled(Text)`
  color: #3C3D3A;
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 8px 0;
  max-width: 100%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 6px;
  right: 8px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6E6E6E;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
    color: #3C3D3A;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  cursor: pointer;
  color: #6E6E6E;
  padding: 4px 8px;
  border-radius: 4px;
  text-decoration: underline;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
    color: #3C3D3A;
    text-decoration: none;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const OverlayNoteBox = ({ show, message, autoDismissTime, showDontShowAgain, onDismiss }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);

    // Auto-dismiss the notification after the specified time
    let timer;
    if (show && autoDismissTime > 0) {
      timer = setTimeout(() => {
        setIsVisible(false);
        if (onDismiss) onDismiss(false);
      }, autoDismissTime);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [show, autoDismissTime, onDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss(false);
  };

  const handleDontShowAgain = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss(true);
  };

  return (
    <OverlayNoteBoxContainer show={isVisible}>
      <NoteText>{message}</NoteText>
      <CloseButton onClick={handleDismiss} aria-label={t('common.closeNotification')}>×</CloseButton>
      {showDontShowAgain && (
        <ActionsContainer>
          <ActionButton onClick={handleDontShowAgain}>{t('common.dontShowAgain')}</ActionButton>
        </ActionsContainer>
      )}
    </OverlayNoteBoxContainer>
  );
};

OverlayNoteBox.propTypes = {
  show: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  autoDismissTime: PropTypes.number,
  showDontShowAgain: PropTypes.bool,
  onDismiss: PropTypes.func
};

OverlayNoteBox.defaultProps = {
  autoDismissTime: 8000,
  showDontShowAgain: false,
  onDismiss: null
};

export default OverlayNoteBox;
